import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './TokenGate.css';
import Loader from './Loader';
import { getDeviceId } from '../services/deviceId';

export default function TokenGate({ children }) {
    const location = useLocation();

    const path = location.pathname.toLowerCase().replace(/\/$/, '');
    const isPublicRoute = ['/login', '/register', '/reset-password'].includes(path);

    const isLicenseValid = (expireStr) => {
        if (!expireStr) return false;
        if (String(expireStr).trim() === '9999-99-99') return true;

        try {
            let expireDate = new Date(expireStr);

            if (isNaN(expireDate.getTime())) {
                const parts = expireStr.split('-');
                if (parts.length === 3) {
                    expireDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                }
            }

            if (isNaN(expireDate.getTime())) return false;

            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const expiryDay = new Date(expireDate.getFullYear(), expireDate.getMonth(), expireDate.getDate());

            return expiryDay >= today;
        } catch {
            return false;
        }
    };

    const [isAuthorized, setIsAuthorized] = useState(() => {
        const expiryStr = localStorage.getItem('subscription_expiry');
        const valid = isLicenseValid(expiryStr);
        if (valid) return true;
        return null;
    });

    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [activating, setActivating] = useState(false);

    useEffect(() => {
        let active = true;

        if (isPublicRoute) {
            return;
        }

        const checkAuthorization = async () => {
            const expiryStr = localStorage.getItem('subscription_expiry');
            const hasValidCache = isLicenseValid(expiryStr);

            if (hasValidCache) {
                try {
                    const response = await fetch('/api/getList');
                    if (response.status === 401 || response.status === 403) {
                        localStorage.removeItem('subscription_expiry');
                        if (active) setIsAuthorized(false);
                    }
                } catch {
                    return;
                }
                return;
            }

            try {
                const response = await fetch('/api/getList');
                if (response.ok) {
                    if (active) setIsAuthorized(true);
                } else if (response.status === 401 || response.status === 403) {
                    if (active) setIsAuthorized(false);
                } else {
                    if (active) setIsAuthorized(true);
                }
            } catch (err) {
                console.error("[TokenGate] Network error during check:", err.message);
                if (active) setIsAuthorized(false);
            }
        };

        checkAuthorization();

        return () => {
            active = false;
        };
    }, [isPublicRoute]);

    const handleActivate = async (e) => {
        e.preventDefault();
        setActivating(true);
        setError('');

        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Notflix-Device-Id': getDeviceId(),
                },
                body: JSON.stringify({ code, device_id: getDeviceId() }),
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                const expiry = data.expire_date;

                if (expiry) {
                    localStorage.setItem('subscription_expiry', expiry);
                }

                window.location.href = '/';
            } else {
                const data = await response.json().catch(() => ({}));
                setError(data.message || 'Codice non valido o errore server.');
            }
        } catch {
            setError('Errore di connessione. Riprova.');
        } finally {
            setActivating(false);
        }
    };

    if (isPublicRoute) {
        return <>{children}</>;
    }

    if (isAuthorized === null) {
        return (
            <div className="token-gate-loader">
                <Loader />
            </div>
        );
    }

    if (isAuthorized) {
        return <>{children}</>;
    }

    return (
        <div className="token-gate-overlay">
            <div className="token-gate-content">
                <div className="token-gate-logo">Notflix</div>
                <h1 className="token-gate-title">Dispositivo non Autorizzato</h1>
                <p style={{ marginBottom: '1.5rem', color: '#ccc' }}>
                    Inserisci il codice del tuo abbonamento per accedere.
                </p>

                <form onSubmit={handleActivate} className="token-gate-input-group">
                    <input
                        type="text"
                        className="token-gate-input"
                        placeholder="Codice Abbonamento"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="token-gate-button"
                        disabled={activating || !code.trim()}
                    >
                        {activating ? 'Attivazione in corso...' : 'Attiva'}
                    </button>
                    {error && <div className="token-gate-error">{error}</div>}
                </form>
            </div>
        </div>
    );
}
